import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { departments, type DepartmentItem } from "../../../../services/admin";
import { ensurePermission } from "../../../../utils/permissions";
import RIcon from "../../../../components/RIcon";

export default function DepartmentsPage() {
  const { user } = useOutletContext<{ user: import("../../../../services/auth").UserInfo | null }>();
  const [deptList, setDeptList] = useState<DepartmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDept, setEditDept] = useState<DepartmentItem | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const loadDepts = async () => {
    try {
      const data = await departments.list();
      setDeptList(data);
    } catch {
      showToast("加载部门列表失败");
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await departments.list();
        setDeptList(data);
      } catch {
        showToast("加载数据失败");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "请输入部门名称";
    return errs;
  };

  const handleCreate = async (e: React.FormEvent) => {
    if (!ensurePermission(user, "departments:write", showToast)) return;
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      await departments.create({
        name: form.name,
        description: form.description || undefined,
      });
      setCreateOpen(false);
      setForm({ name: "", description: "" });
      setErrors({});
      await loadDepts();
      showToast("部门创建成功");
    } catch {
      showToast("创建部门失败");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    if (!ensurePermission(user, "departments:write", showToast)) return;
    e.preventDefault();
    if (!editDept) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      await departments.update(editDept.id, { name: form.name, description: form.description || undefined });
      setEditDept(null);
      setForm({ name: "", description: "" });
      setErrors({});
      await loadDepts();
      showToast("部门已更新");
    } catch {
      showToast("更新部门失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!ensurePermission(user, "departments:delete", showToast)) return;
    try {
      await departments.delete(id);
      setDeleteId(null);
      await loadDepts();
      showToast("部门已删除");
    } catch {
      showToast("删除部门失败");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RIcon name="loader-4-line" className="animate-spin text-2xl text-[#0EA5A0]" />
        <span className="ml-2 text-[#718096]">加载中...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />{toast}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#1A2B3C]">部门管理</h2>
          <p className="text-xs text-[#718096] mt-0.5">管理企业组织架构中的各业务部门</p>
        </div>
        <button onClick={() => ensurePermission(user, "departments:write", showToast) && setCreateOpen(true)} className="flex items-center gap-2 px-4 h-9 bg-[#0EA5A0] text-white rounded-lg text-sm font-medium hover:bg-[#0D9590] cursor-pointer whitespace-nowrap">
          <RIcon name="add-line" />新建部门
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-[#F4F8FB]">
                {["部门名称", "人员数量", "描述", "操作"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#718096] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deptList.map((dept) => (
                <tr key={dept.id} className="border-b border-gray-50 hover:bg-[#F4F8FB]/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-[#0EA5A0]/10 flex items-center justify-center flex-shrink-0">
                        <RIcon name="building-line" className="text-[#0EA5A0] text-sm" />
                      </div>
                      <span className="font-medium text-[#1A2B3C] whitespace-nowrap">{dept.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[#1A2B3C] font-semibold">{dept.user_count}</span>
                    <span className="text-[#718096] text-xs ml-1">人</span>
                  </td>
                  <td className="px-4 py-3 text-[#718096] text-xs max-w-52">
                    <span className="truncate block">{dept.description || "-"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => ensurePermission(user, "departments:write", showToast) && (() => { setEditDept(dept); setForm({ name: dept.name, description: dept.description || "" }); setErrors({}); })()} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#718096] hover:text-red-500 cursor-pointer">
                        <RIcon name="edit-line" className="text-sm" />
                      </button>
                      <button onClick={() => ensurePermission(user, "departments:delete", showToast) && setDeleteId(dept.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#718096] hover:text-red-500 cursor-pointer">
                        <RIcon name="delete-bin-line" className="text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {deptList.length === 0 && (
            <div className="text-center py-10 text-[#718096] text-sm">暂无部门数据</div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">新建部门</h3>
              <button onClick={() => setCreateOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">部门名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="如：研发部"
                  className={`w-full border rounded-lg px-3 h-10 text-sm outline-none ${errors.name ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"}`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">部门描述</label>
                <textarea
                  rows={3}
                  placeholder="描述部门职能..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0EA5A0] resize-none"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={500}
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setCreateOpen(false)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button type="submit" disabled={saving} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                  {saving ? <><RIcon name="loader-4-line" className="animate-spin" />创建中...</> : "创建部门"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



      {editDept && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">编辑部门</h3>
              <button onClick={() => setEditDept(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">部门名称 <span className="text-red-500">*</span></label>
                <input type="text" className={`w-full border rounded-lg px-3 h-10 text-sm outline-none ${errors.name ? "border-red-400" : "border-gray-200 focus:border-[#0EA5A0]"}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">部门描述</label>
                <textarea rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0EA5A0] resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditDept(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                <button type="submit" disabled={saving} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">{saving ? <><RIcon name="loader-4-line" className="animate-spin" />保存中...</> : "保存修改"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><RIcon name="delete-bin-line" className="text-2xl text-red-500" /></div>
            <h3 className="text-base font-semibold text-[#1A2B3C] mb-1">确认删除部门</h3>
            <p className="text-sm text-[#718096] mb-6">删除部门前请确保已处理该部门下的所有人员</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 h-10 bg-red-500 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
